import type { GQL } from "@binale-tech/shared";

import type { AgentContext, AgentIO } from "../runner/types";

export const AI_POLL_MS = 1000;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const isSettled = (doc: GQL.IDocument | undefined, errorCountsFrom: number): boolean => {
    const status = doc?.aiStatus?.status;
    if (status === "done") return true;
    return status === "error" && Date.now() >= errorCountsFrom;
};

export interface AiProgressOutcome {
    done: number;
    failed: number;
    /** Ids with no document or no aiStatus after the grace period: AI was never started for them. */
    notStarted: number;
    /** Ids still being read when the wait ended (cancel or stall). */
    running: number;
    total: number;
    /** True when the modal was closed before every document settled. */
    cancelled: boolean;
    /** True when nothing changed for STALL_MS; the still running ids continue on the server. */
    stalled: boolean;
}

export const NOT_STARTED_GRACE_MS = 30_000;
export const STALL_MS = 600_000;

/**
 * Polls the live documents map until every id is done, failed or never started, or the run is cancelled or stalls.
 * `errorGraceMs` keeps a stale error from before a retry from counting as settled.
 */
export const waitForAi = async (
    ctx: AgentContext,
    io: AgentIO,
    ids: string[],
    progressId: string,
    errorGraceMs = 0,
): Promise<AiProgressOutcome> => {
    const startedAt = Date.now();
    const errorCountsFrom = startedAt + errorGraceMs;
    const measure = () => {
        const docs = ctx.documents();
        let done = 0;
        let failed = 0;
        let notStarted = 0;
        for (const id of ids) {
            const doc = docs.get(id);
            if (!doc?.aiStatus) {
                if (Date.now() - startedAt >= NOT_STARTED_GRACE_MS) notStarted += 1;
            } else if (isSettled(doc, errorCountsFrom)) {
                if (doc.aiStatus.status === "done") done += 1;
                else failed += 1;
            }
        }
        return { done, failed, notStarted };
    };

    let lastSettled = -1;
    let lastChange = startedAt;
    for (;;) {
        await sleep(AI_POLL_MS);
        const { done, failed, notStarted } = measure();
        const settledCount = done + failed + notStarted;
        io.update(progressId, { progress: { done: settledCount, total: ids.length } });
        if (settledCount !== lastSettled) {
            lastSettled = settledCount;
            lastChange = Date.now();
        }
        const result = { done, failed, notStarted, running: ids.length - settledCount, total: ids.length };
        if (settledCount === ids.length) return { ...result, cancelled: false, stalled: false };
        if (io.isCancelled()) return { ...result, cancelled: true, stalled: false };
        if (Date.now() - lastChange >= STALL_MS) return { ...result, cancelled: false, stalled: true };
    }
};
